/**
 * SystemModuleFieldsPanel — F12.M Etap C.3.
 *
 * Panel zarządzania polami modułu systemowego (zakładka „Pola dodatkowe" w hub Settings).
 *
 * Funkcjonalność:
 *  - Lista core fields (system-locked, oznaczone badge „Systemowe") + ext fields
 *  - „Dodaj pole" → `AddSystemFieldModal`
 *  - Soft-delete dla ext fields (core blokowane przez backend → 403)
 *  - Edit pola — placeholder w C.3, dochodzi w D wraz z ALTER TABLE
 *
 * Dane: `GET /api/system-modules/:slug` (cały snapshot — fields w polu .fields)
 * Mutations: `DELETE /api/system-modules/:slug/fields/:fieldId` (soft-delete)
 */

import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DefaultValueUnavailableReason,
  type FieldValueDomain,
  SYSTEM_EXT_FIELD_TYPE_SET,
} from '@saas/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Lock, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

import {
  AddSystemFieldModal,
  type SystemFieldType,
  systemFieldTypeI18nKey,
} from './AddSystemFieldModal';
import { DefaultValueCell } from './DefaultValueCell';
import { useFieldDefaultSaver } from './useFieldDefaultSaver';

export interface SystemModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  description: string | null;
  isCore: boolean;
  isSystemLocked: boolean;
  isRequired: boolean;
  isUnique: boolean;
  isSearchable: boolean;
  isFilterable: boolean;
  showInList: boolean;
  sortOrder: number;
  /** Wartość domyślna formularza nowego rekordu (format: `@saas/shared` `default-value`). */
  defaultValue: string | null;
  /** Wartość domyślna nowego rekordu — czy pole może ją mieć, z jakiej dziedziny, a jeśli nie — dlaczego. */
  defaultable: boolean;
  valueDomain: FieldValueDomain | null;
  notDefaultableReason: DefaultValueUnavailableReason | null;
}

interface SystemModuleFieldsPanelProps {
  moduleSlug: string;
  fields: SystemModuleField[];
}

export function SystemModuleFieldsPanel({ moduleSlug, fields }: SystemModuleFieldsPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SystemModuleField | null>(null);
  const defaultSaver = useFieldDefaultSaver();

  const deleteMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      await api.delete(`/system-modules/${moduleSlug}/fields/${fieldId}`);
    },
    onSuccess: async () => {
      toast.show({ variant: 'success', title: t('tenant.systemModuleFields.toasts.deleted') });
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['system-module', moduleSlug] }),
        // Analogicznie do AddSystemFieldModal.tsx (bug 2026-07-14, E.2) — usunięte pole
        // musi też zniknąć z listy pól-rodzeństwa (edytor wiązań `record_field`), nie
        // tylko z głównej listy pól.
        queryClient.invalidateQueries({ queryKey: ['system-module-fields', moduleSlug] }),
      ]);
    },
    onError: (err) => {
      const isCoreLocked = isAxiosError(err) && err.response?.data?.error === 'CORE_FIELD_LOCKED';
      toast.show({
        variant: 'error',
        title: isCoreLocked
          ? t('tenant.systemModuleFields.toasts.cannotDeleteCore')
          : t('tenant.systemModuleFields.toasts.deleteError'),
      });
    },
  });

  // Posortuj: core najpierw, potem ext, w obrębie grup po sortOrder
  const sortedFields = [...fields].sort((a, b) => {
    if (a.isCore !== b.isCore) return a.isCore ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });

  return (
    <div className="space-y-4" data-testid="system-module-fields-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">
            {t('tenant.systemModuleFields.title')}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('tenant.systemModuleFields.subtitle')}
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddModal(true)}
          data-testid="system-module-fields-add-btn"
        >
          {t('tenant.systemModuleFields.add')}
        </Button>
      </div>

      {sortedFields.length === 0 ? (
        <Card padding="lg" className="text-center">
          <p className="text-sm text-text-tertiary">{t('tenant.systemModuleFields.empty')}</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="system-module-fields-list">
          {sortedFields.map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              onDelete={() => setPendingDelete(field)}
              defaultCell={
                <DefaultValueCell
                  moduleSlug={moduleSlug}
                  field={field}
                  value={defaultSaver.valueOf(field.id, field.defaultValue)}
                  onChange={(next) => defaultSaver.save(moduleSlug, field.id, next)}
                />
              }
            />
          ))}
        </div>
      )}

      <AddSystemFieldModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        moduleSlug={moduleSlug}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('common.delete')}
        message={
          pendingDelete
            ? t('tenant.systemModuleFields.confirmDelete', { name: pendingDelete.name })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ─── Field row ──────────────────────────────────────────────────────────────

interface FieldRowProps {
  field: SystemModuleField;
  onDelete: () => void;
  /** Komórka „Wartość domyślna" (prawa kolumna wiersza). */
  defaultCell: ReactNode;
}

function FieldRow({ field, onDelete, defaultCell }: FieldRowProps) {
  const { t } = useTranslation();
  const typeKey = SYSTEM_TYPE_KEYS.has(field.fieldType)
    ? systemFieldTypeI18nKey(field.fieldType as SystemFieldType)
    : null;

  return (
    <Card
      padding="sm"
      className="flex items-center gap-3"
      data-testid={`system-module-field-${field.slug}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-text-primary">{field.name}</span>
          {field.isSystemLocked && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-muted"
              title={t('tenant.systemModuleFields.lockedTooltip')}
            >
              <Lock className="h-3 w-3" />
              {t('tenant.systemModuleFields.coreBadge')}
            </span>
          )}
          {field.isRequired && (
            <span className="rounded-full bg-error-100 px-2 py-0.5 text-[10px] font-medium text-error-700 dark:bg-error-900/30 dark:text-error-300">
              {t('tenant.systemModuleFields.requiredBadge')}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-text-tertiary">
          <code className="rounded bg-bg-tertiary px-1 py-0.5 font-mono text-[11px]">
            {field.slug}
          </code>{' '}
          · {typeKey ? t(typeKey) : field.fieldType}
        </p>
        {field.description && (
          <p className="mt-1 text-xs text-text-tertiary line-clamp-2">{field.description}</p>
        )}
      </div>
      <div className="w-64 shrink-0">
        <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
          {t('tenant.systemModuleFields.defaultValue.label')}
        </p>
        {defaultCell}
      </div>
      {!field.isSystemLocked && (
        <IconButton
          size="sm"
          onClick={onDelete}
          aria-label={t('common.delete')}
          className="text-text-muted hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          data-testid={`system-module-field-${field.slug}-delete-btn`}
        >
          <Trash2 />
        </IconButton>
      )}
    </Card>
  );
}

// Źródło prawdy: packages/shared/src/field-types.ts
const SYSTEM_TYPE_KEYS: ReadonlySet<string> = SYSTEM_EXT_FIELD_TYPE_SET;
